import * as React from 'react';
import styled from 'styled-components';
import { CompanyScore, CriterionScore } from '../../../atoms/calculatedFields';
import { BorderColor, DarkGreySidely2, GreySidely4, RedSidely, SidelyBlack } from '../../../styles/global/css/Utils';
import { Translate, translateToString } from '../../../styles/global/translate';

const Wrapper = styled.div`
	display: flex;
	flex-flow: column;
	gap: 18px;
	padding: 0 20px 20px;
	width: 100%;
`;

const Card = styled.div`
	display: flex;
	flex-flow: column;
	gap: 10px;
	padding: 14px 16px;
	border: 1px solid ${BorderColor};
	border-radius: 6px;
`;

const Head = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: 10px;
`;

const Name = styled.h4`
	margin: 0;
	font-size: 14px;
	font-weight: 600;
	color: ${SidelyBlack};
`;

const Share = styled.span`
	font-size: 20px;
	font-weight: 600;
	color: ${SidelyBlack};
`;

const Row = styled.div<{ muted?: boolean }>`
	display: flex;
	justify-content: space-between;
	gap: 12px;
	font-size: 13px;
	color: ${p => p.muted ? GreySidely4 : DarkGreySidely2};
`;

const Rule = styled.div`
	height: 1px;
	background-color: ${BorderColor};
`;

const Aside = styled.p`
	margin: 0;
	font-size: 11px;
	color: ${GreySidely4};
`;

/// One decimal: more digits would suggest a precision the criteria do not have.
function round(value: number): string {
	return (Math.round(value * 10) / 10).toString();
}

/// A criterion reads as what it gave out of what it could have given, with the
/// value it was computed from in brackets. One that did not apply says so
/// rather than showing a zero it did not earn.
function criterion(entry: CriterionScore): JSX.Element {
	return <Row key={`criterion[${entry.field}][${entry.name}]`} muted={entry.points === null}>
		<span>{entry.name}{entry.value === null ? '' : ` (${round(entry.value)})`}</span>
		<span>
			{entry.points === null
				? translateToString('score.not_measured')
				: `${round(entry.points * entry.weight)} / ${round(entry.weight * 100)}`}
		</span>
	</Row>;
}

/// Everything a company was scored on, one block per scorecard: the share, the
/// points behind it, every criterion that produced them, the penalties that
/// fired, and how much of the card could be measured here at all.
export default function CompanyScores(props: { scores: CompanyScore[] }): JSX.Element {
	return <Wrapper>
		{props.scores.map(({ field_id, field_name, scored }) => <Card key={`score[${field_id}]`}>
			<Head>
				<Name>{field_name}</Name>
				<Share>{scored.score === null ? translateToString('score.not_scored') : `${round(scored.score)} / 100`}</Share>
			</Head>
			<Row>
				<Translate id='score.points' />
				<span>{round(scored.earned)} / {round(scored.earnable)}</span>
			</Row>
			<Rule />
			{scored.breakdown.map(criterion)}
			{scored.penalties.map(penalty => <Row key={`penalty[${penalty.field}][${penalty.name}]`} style={{ color: RedSidely }}>
				<span>{penalty.name}</span>
				<span>- {round(penalty.points)}</span>
			</Row>)}
			{/* a share earned on a third of the card is a different claim from the
			    same share earned on all of it: say which this is */}
			<Aside>{translateToString('score.coverage', [['PERCENT', round(scored.coverage * 100)]])}</Aside>
		</Card>)}
	</Wrapper>;
}
